import type { RenderComponentProps } from "../../../shared/types";

import * as React from "react";

import {
  Hovercard,
  HovercardAnchor,
  HovercardArrow,
  HovercardProvider,
  useHovercardStore,
} from "@ariakit/react";
import twMerge from "@replo/design-system/utils/twMerge";
import classNames from "classnames";
import { parseInteger } from "replo-utils/lib/math";

import {
  RuntimeHooksContext,
  useRuntimeContext,
} from "../../../shared/runtime-context";
import { useComponentClassNames } from "../../../shared/utils/classNames";
import { mergeContext } from "../../../shared/utils/context";
import useSharedState from "../../../store/hooks/useSharedState";
import { ReploComponent } from "../ReploComponent";

type BaseSide = "top" | "bottom" | "left" | "right";
type Alignment = "start" | "center" | "end";
type Side = BaseSide | `${BaseSide}-${Exclude<Alignment, "center">}`;

const Tooltip: React.FC<RenderComponentProps> = (props) => {
  const { component, context, componentAttributes } = props;
  const isEditorCanvas =
    useRuntimeContext(
      RuntimeHooksContext,
    ).useIsEditorEditModeRenderEnvironment();
  const [isOpen, setIsOpen] = useSharedState([component.id, "isOpen"], false);
  const hovercard = useHovercardStore();
  const tooltipContentComponent =
    props.component.props._tooltipContentComponent!;
  const children = component.children ?? [];
  const contentClassNameMap = useComponentClassNames(
    "tooltipContent",
    tooltipContentComponent,
    context,
  );

  const nextContext = mergeContext(context, {
    state: {
      tooltip: {
        isOpen,
      },
    },
  });

  const contentConfig = {
    _showArrow: component.props._showArrow as boolean,
    _arrowSize: parseInteger(component.props._arrowSize as string),
    _side: component.props._side as BaseSide,
    _alignment: component.props._alignment as Alignment,
    _sideOffset: component.props._sideOffset as string,
    _alignOffset: component.props._alignOffset as string,
    _hoverDelayTime: component.props._hoverDelayTime as number,
    _triggeringAction: component.props._triggeringAction,
  };

  const getSideProp = (): Side => {
    const side = contentConfig._side ?? "top";
    const _alignment = contentConfig._alignment ?? "center";
    if (_alignment === "center") {
      return side;
    }
    return `${side}-${_alignment}`;
  };

  const useOnClick = contentConfig._triggeringAction === "onClick";

  // NOTE (Sebas, 2024-07-04): This is in charge of re-rendering the hovercard
  // when the arrow size changes. If not, the arrow position will be wrong.
  // biome-ignore lint/correctness/useExhaustiveDependencies: extra deps component.props._arrowSize
  React.useEffect(() => {
    if (isEditorCanvas) {
      hovercard.render();
    }
  }, [component.props._arrowSize, hovercard, isEditorCanvas]);

  return (
    <HovercardProvider
      store={hovercard}
      open={isOpen}
      timeout={useOnClick ? 0 : contentConfig._hoverDelayTime ?? 0}
      setOpen={(value) => {
        if (!isEditorCanvas) {
          setIsOpen(value);
        }
      }}
      placement={getSideProp()}
    >
      <HovercardAnchor
        showOnHover={!useOnClick}
        render={(props) => {
          return (
            <div
              {...componentAttributes}
              {...props}
              onClick={() => {
                if (useOnClick) {
                  setIsOpen(!isOpen);
                }
              }}
              className={twMerge(useOnClick && "cursor-pointer")}
            >
              {children?.map((child) => (
                <ReploComponent
                  key={child.id}
                  component={child}
                  context={nextContext}
                />
              ))}
            </div>
          );
        }}
      />
      <Hovercard
        gutter={parseInteger(contentConfig._sideOffset)}
        className={contentClassNameMap?.contentWrapper}
        shift={parseInteger(contentConfig._alignOffset)}
        hideOnHoverOutside={!useOnClick}
        style={{
          // NOTE (Sebas, 2025-01-23): I'm setting this to the maximum value
          // to ensure that the tooltip is displayed above other elements.
          // We only want to apply this on the published page because this
          // zIndex will prevent the user from editing the tooltip content.
          zIndex: !isEditorCanvas ? 2_147_482_645 : undefined,
        }}
      >
        <ReploComponent
          context={nextContext}
          component={tooltipContentComponent}
        />
        {contentConfig._showArrow && (
          <HovercardArrow
            size={
              !Boolean(contentConfig._arrowSize) ||
              Number.isNaN(contentConfig._arrowSize)
                ? 20
                : contentConfig._arrowSize
            }
            className={classNames("tooltip-arrow", contentClassNameMap?.arrow)}
          />
        )}
      </Hovercard>
    </HovercardProvider>
  );
};

export default Tooltip;
